import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import {
  containerStyles,
  mainContainerStyles,
  searchBoxStyles,
  searchInputStyles,
  searchButtonStyles,
  loadingContainerStyles,
  glassPaperStyles,
  profileIdStyles,
  markdownContentStyles,
  noProfileContainerStyles,
  noProfilePaperStyles,
  chatPromptStyles,
  chatPromptTextStyles,
} from './UnderwriterStyles';
import { ProfileData } from './Underwriter.types';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAIStoryById } from './UnderwriterActions';
import { resetAIStory } from './UnderwriterSlice';
import { RootState } from '../../store/store';
import ReactMarkdown from "react-markdown";
import InsureAssist from '../../customers/Dashboard/InsureAssist/InsureAssist';

const ProfileChatUI: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchId, setSearchId] = useState('');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const previousIdRef = useRef<string>('');
  const [isInsureAssistOpen, setIsInsureAssistOpen] = useState(false);
  const [isInsureAssistDisabled, setIsInsureAssistDisabled] = useState(false);
  const [typed, setTyped] = useState<any>(undefined);

  const { AIstoryById } = useAppSelector(
    (state: RootState) => state.underwriterAIStory
  );

  const { isLoading } = useAppSelector(
    (state: RootState) => state.common
  );

  useEffect(() => {
    return () => {
      dispatch(resetAIStory());
      setProfile(null);
    };
  }, [dispatch]);

  useEffect(() => {
    if (AIstoryById) {
      setProfile({
        id: searchId.trim(),
        bio: AIstoryById,
      });
    }
  }, [AIstoryById, searchId]);

  const handleSearch = () => {
    if (searchId.trim() && searchId.trim() !== previousIdRef.current) {
      previousIdRef.current = searchId.trim();
      dispatch(getAIStoryById(searchId.trim()));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleChatPromptClick = () => {
    setIsInsureAssistOpen(true);
  };

  const handleInsureAssistClose = () => {
    setIsInsureAssistOpen(false);
    setIsInsureAssistDisabled(false);
  };

  return (
    <Box sx={containerStyles}>
      <Box sx={mainContainerStyles}>
        <Box sx={searchBoxStyles}>
          <TextField
            label="Enter ID"
            variant="outlined"
            size="small"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={searchInputStyles}
          />
          <IconButton 
            onClick={handleSearch} 
            color="primary"
            sx={searchButtonStyles}
          >
            <SendIcon />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box sx={loadingContainerStyles}>
            <CircularProgress size={20} />
            <Typography variant="body2">Preparing profile information...</Typography>
          </Box>
        ) : profile ? (
          <Paper elevation={0} sx={glassPaperStyles}>
            <Typography variant="h6" sx={profileIdStyles}>
              ID: {profile.id}
            </Typography>
            <Box sx={markdownContentStyles}>
              <ReactMarkdown>{profile.bio}</ReactMarkdown>
            </Box>
          </Paper>
        ) : (
          <Box sx={noProfileContainerStyles}>
            <Paper elevation={0} sx={noProfilePaperStyles}>
              <Typography variant="body1" color="textSecondary">
                No Profile loaded
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {profile && !isInsureAssistOpen && (
        <Box sx={chatPromptStyles} onClick={handleChatPromptClick}>
          <ChatIcon color="primary" />
          <Typography sx={chatPromptTextStyles}>
            Need more information? Chat here!
          </Typography>
        </Box>
      )}

      <InsureAssist
        isOpen={isInsureAssistOpen}
        setIsOpen={handleInsureAssistClose}
        setIsDisabled={setIsInsureAssistDisabled}
        typed={typed}
        searchedCustomerId={profile?.id}
      />
    </Box>
  );
};

export default ProfileChatUI;
